/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { 
  Bell, Clock, Mail, MessageSquare, Phone, 
  Video, Calendar, User, AlertTriangle, CheckCircle, 
  XCircle, Send, Eye,  Search, ChevronDown, ChevronUp,
   Users, Briefcase, CalendarDays } from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';
import { dummyReminders } from '../../data/dummyReminders';
import { Reminder } from '../../types';

export default function Reminders() {
  const { state } = useAppContext();
  const { appointments } = state;

  const [reminders] = useState<Reminder[]>(dummyReminders);
  const [selectedReminder, setSelectedReminder] = useState<Reminder | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'scheduled' | 'sent' | 'delivered' | 'failed'>('all');
  const [filterType, setFilterType] = useState<'all' | 'appointment' | 'educational' | 'video' | 'follow-up'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailedReminders, setShowDetailedReminders] = useState(false);

  const filteredReminders = reminders.filter(reminder => {
    const matchesSearch = reminder.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         reminder.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || reminder.status === filterStatus;
    const matchesType = filterType === 'all' || reminder.type === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusColor = (status: Reminder['status']) => {
    switch (status) {
      case 'scheduled': return 'text-blue-600 bg-blue-100';
      case 'sent': return 'text-yellow-600 bg-yellow-100';
      case 'delivered': return 'text-green-600 bg-green-100';
      case 'failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: Reminder['status']) => {
    switch (status) {
      case 'scheduled': return <Clock className="w-4 h-4" />;
      case 'sent': return <Send className="w-4 h-4" />;
      case 'delivered': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getTypeIcon = (type: Reminder['type']) => {
    switch (type) {
      case 'appointment': return <Calendar className="w-5 h-5 text-blue-500" />;
      case 'educational': return <Mail className="w-5 h-5 text-green-500" />;
      case 'video': return <Video className="w-5 h-5 text-purple-500" />;
      case 'follow-up': return <Phone className="w-5 h-5 text-orange-500" />;
      default: return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  const getMethodIcon = (method: Reminder['method']) => {
    switch (method) {
      case 'sms': return <MessageSquare className="w-4 h-4" />;
      case 'email': return <Mail className="w-4 h-4" />;
      case 'call': return <Phone className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  // const getPriorityColor = (priority: Reminder['priority']) => {
  //   switch (priority) {
  //     case 'high': return 'text-red-600';
  //     case 'medium': return 'text-yellow-600';
  //     case 'low': return 'text-green-600';
  //     default: return 'text-gray-600';
  //   }
  // };

  const formatDateTime = (date: string, time: string) => {
    const dateObj = new Date(`${date}T${time}`);
    return dateObj.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const upcomingReminders = reminders.filter(r => r.status === 'scheduled').length;
  const sentReminders = reminders.filter(r => r.status === 'sent' || r.status === 'delivered').length;
  const failedReminders = reminders.filter(r => r.status === 'failed').length;

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const todayStr = today.toISOString().split('T')[0];
  const tomorrowStr = tomorrow.toISOString().split('T')[0];

  const todayAppointments = appointments.filter(apt => apt.date === todayStr);
  const tomorrowAppointments = appointments.filter(apt => apt.date === tomorrowStr);

  const upcomingMeetings = 3;
  const doctorsOnSchedule = 4;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Reminders & Schedule</h2>
            <p className="text-gray-600">Daily overview of appointments, meetings, and reminders</p>
          </div>
          <div className="flex items-center space-x-2">
            <Bell className="w-8 h-8 text-green-500" />
          </div>
        </div>

        {/* Priority Reminders */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-blue-900">Today's Appointments</h3>
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-blue-900 mb-2">{todayAppointments.length}</p>
            <div className="space-y-1">
              {todayAppointments.slice(0, 3).map((apt) => (
                <div key={apt.id} className="text-xs text-blue-800 bg-blue-50 rounded px-2 py-1 truncate">
                  {apt.time} - {apt.patientName}
                </div>
              ))}
              {todayAppointments.length > 3 && (
                <div className="text-xs text-blue-700 font-medium">
                  +{todayAppointments.length - 3} more
                </div>
              )}
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-green-900">Tomorrow's Appointments</h3>
              <CalendarDays className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-3xl font-bold text-green-900 mb-2">{tomorrowAppointments.length}</p>
            <div className="space-y-1">
              {tomorrowAppointments.slice(0, 3).map((apt) => (
                <div key={apt.id} className="text-xs text-green-800 bg-green-50 rounded px-2 py-1 truncate">
                  {apt.time} - {apt.patientName}
                </div>
              ))}
              {tomorrowAppointments.length > 3 && (
                <div className="text-xs text-green-700 font-medium">
                  +{tomorrowAppointments.length - 3} more
                </div>
              )}
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-purple-900">Upcoming Meetings</h3>
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <p className="text-3xl font-bold text-purple-900 mb-2">{upcomingMeetings}</p>
            <div className="space-y-1">
              <div className="text-xs text-purple-800 bg-purple-50 rounded px-2 py-1">
                10:00 AM - Staff Meeting
              </div>
              <div className="text-xs text-purple-800 bg-purple-50 rounded px-2 py-1">
                2:00 PM - Team Review
              </div>
              <div className="text-xs text-purple-800 bg-purple-50 rounded px-2 py-1">
                4:30 PM - Case Discussion
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-5 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-orange-900">Doctors on Schedule</h3>
              <Briefcase className="w-6 h-6 text-orange-600" />
            </div>
            <p className="text-3xl font-bold text-orange-900 mb-2">{doctorsOnSchedule}</p>
            <div className="space-y-1">
              <div className="text-xs text-orange-800 bg-orange-50 rounded px-2 py-1">
                Dr. Estrabillo - Full Day
              </div>
              <div className="text-xs text-orange-800 bg-orange-50 rounded px-2 py-1">
                Dr. Martinez - Morning
              </div>
              <div className="text-xs text-orange-800 bg-orange-50 rounded px-2 py-1">
                Dr. Chen - Afternoon
              </div>
            </div>
          </div>
        </div>

        {/* Collapsible Detailed Reminders Section */}
        <div className="border-t border-gray-200 pt-6">
          <button
            onClick={() => setShowDetailedReminders(!showDetailedReminders)}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <div className="flex items-center space-x-3">
              <Bell className="w-5 h-5 text-gray-700" />
              <span className="font-semibold text-gray-900">Patient Reminders</span>
              <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                {reminders.length} Total
              </span>
            </div>
            {showDetailedReminders ? (
              <ChevronUp className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            )}
          </button>

          {showDetailedReminders && (
            <div className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blue-600">Scheduled</p>
                      <p className="text-2xl font-bold text-blue-900">{upcomingReminders}</p>
                    </div>
                    <Clock className="w-8 h-8 text-blue-500" />
                  </div>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-green-600">Sent/Delivered</p>
                      <p className="text-2xl font-bold text-green-900">{sentReminders}</p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                </div>

                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-red-600">Failed</p>
                      <p className="text-2xl font-bold text-red-900">{failedReminders}</p>
                    </div>
                    <XCircle className="w-8 h-8 text-red-500" />
                  </div>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-purple-600">Total</p>
                      <p className="text-2xl font-bold text-purple-900">{reminders.length}</p>
                    </div>
                    <Bell className="w-8 h-8 text-purple-500" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Filters and Search - Only show when expanded */}
      {showDetailedReminders && (
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by patient name or reminder title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="all">All Status</option>
            <option value="scheduled">Scheduled</option>
            <option value="sent">Sent</option>
            <option value="delivered">Delivered</option>
            <option value="failed">Failed</option>
          </select>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="all">All Types</option>
            <option value="appointment">Appointment</option>
            <option value="educational">Educational</option>
            <option value="video">Video</option>
            <option value="follow-up">Follow-up</option>
          </select>
        </div>

        {/* Reminders List */}
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {filteredReminders.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No reminders found</p>
              <p className="text-sm text-gray-400">Try adjusting your search or filters</p>
            </div>
          ) : (
            filteredReminders.map((reminder) => (
              <div
                key={reminder.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedReminder(reminder)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="flex-shrink-0">
                      {getTypeIcon(reminder.type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="font-semibold text-gray-900 truncate">{reminder.title}</h3>
                        {reminder.priority === 'high' && (
                          <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        )}
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                        <div className="flex items-center space-x-1">
                          <User className="w-4 h-4" />
                          <span>{reminder.patientName}</span>
                        </div>
                        
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>Appt: {reminder.appointmentDate} at {reminder.appointmentTime}</span>
                        </div>
                        
                        <div className="flex items-center space-x-1">
                          {getMethodIcon(reminder.method)}
                          <span className="capitalize">{reminder.method}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 text-sm">
                        <span className="text-gray-600">
                          Scheduled: {formatDateTime(reminder.scheduledDate, reminder.scheduledTime)}
                        </span>
                        
                        {reminder.caseId && (
                          <span className="text-gray-500">
                            Case: {reminder.caseId}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <span className={`px-2 py-1 text-xs rounded-full flex items-center space-x-1 ${getStatusColor(reminder.status)}`}>
                      {getStatusIcon(reminder.status)}
                      <span className="capitalize">{reminder.status}</span>
                    </span>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedReminder(reminder);
                      }}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        </div>
      )}

      {/* Reminder Details Modal */}
      {selectedReminder && (
        <ReminderDetailsModal
          reminder={selectedReminder}
          onClose={() => setSelectedReminder(null)}
        />
      )}
    </div>
  );
}

// Reminder Details Modal Component
function ReminderDetailsModal({
  reminder,
  onClose
}: {
  reminder: Reminder;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-5/6 overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            {reminder.type === 'appointment' && <Calendar className="w-6 h-6 text-blue-500" />}
            {reminder.type === 'educational' && <Mail className="w-6 h-6 text-green-500" />}
            {reminder.type === 'video' && <Video className="w-6 h-6 text-purple-500" />}
            {reminder.type === 'follow-up' && <Phone className="w-6 h-6 text-orange-500" />}
            <div>
              <h3 className="text-xl font-bold text-gray-900">{reminder.title}</h3>
              <p className="text-sm text-gray-600">
                {reminder.patientName} • {reminder.appointmentType} appointment
              </p>
            </div>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
          >
            <XCircle className="w-6 h-6" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-6">
          {/* Reminder Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Patient</label>
              <p className="text-gray-900 font-semibold">{reminder.patientName}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Appointment</label>
              <p className="text-gray-900">{reminder.appointmentDate} at {reminder.appointmentTime}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled For</label>
              <p className="text-gray-900">
                {new Date(`${reminder.scheduledDate}T${reminder.scheduledTime}`).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Method & Status</label>
              <div className="flex items-center space-x-2">
                <span className="capitalize text-gray-900">{reminder.method}</span>
                <span className="text-gray-400">•</span>
                <span className={`px-2 py-1 text-xs rounded-full ${reminder.status === 'scheduled' ? 'bg-blue-100 text-blue-800' : reminder.status === 'sent' ? 'bg-yellow-100 text-yellow-800' : reminder.status === 'delivered' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {reminder.status}
                </span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <span className={`px-2 py-1 text-xs rounded-full ${reminder.priority === 'high' ? 'bg-red-100 text-red-800' : reminder.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                {reminder.priority}
              </span>
            </div>
            
            {reminder.caseId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Case ID</label>
                <p className="text-gray-900 font-mono text-sm">{reminder.caseId}</p>
              </div>
            )}
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Message Content</label>
            <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto">
              <p className="text-gray-900 whitespace-pre-wrap">{reminder.content}</p>
            </div>
          </div>

          {/* Video URL */}
          {reminder.videoUrl && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Educational Video</label>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <Video className="w-6 h-6 text-purple-500" />
                  <div>
                    <p className="font-medium text-purple-900">Wisdom Teeth Treatment Education</p>
                    <a 
                      href={reminder.videoUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-purple-600 hover:text-purple-800 text-sm underline"
                    >
                      {reminder.videoUrl}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="border-t border-gray-200 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
              <div>
                <span className="font-medium">Created:</span> {reminder.createdAt.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit'
                })}
              </div>
              
              {reminder.sentAt && (
                <div>
                  <span className="font-medium">Sent:</span> {reminder.sentAt.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}